import { Space, Input, Button } from 'antd';
import { useState, ChangeEvent, useEffect } from 'react';
import {
  RolesDrawerType,
  IRBACRole,
  IServicePermissions,
  RbacRoleObjectKey,
  IRBACUserGroup,
  RbacRoleType,
} from '../../../../../model/rbac';
import styles from './index.module.scss';
import getFormatMessage from '../../../../../locales/get-format-message';
import { RolesDrawer } from './roles-drawer';
import classNames from 'classnames';
import { SvgIcon } from '../../../../../components/svg-icon';
import { SvgIconName } from '../../../../../components/svg-icon/model';
import inputSearch from '../../../../../assets/img/input-search.png';
import { CommonOperateModal } from '../../common-modal';
import { deleteRoles } from '../../../../../api/rbac';
import {
  messageError,
  messageSuccess,
} from '../../../../../components/message';
import { PermissionCheck } from '../../../../component/permission-check';
import { AutoTable } from '../../../../../components/auto-table';
import { IListItem } from '../../../../../components/auto-table/model';
import { CustomPopover } from '../../../../../components/custom-popover';
import { auditReadLog, auditWriteLog } from '../../../../lib/aduit-log';
import { IAuditItem } from '../../../../../model/api-modules';

interface IProps {
  readonly loading: boolean;
  readonly rolesList: ReadonlyArray<IRBACRole>;
  readonly studioId: string;
  readonly onUpdate: () => void;
  readonly permissionsList: IServicePermissions | null;
  readonly groupsList: ReadonlyArray<IRBACUserGroup>;
}
export const RolesComponent = (props: IProps) => {
  const [editType, setEditType] = useState<RolesDrawerType | null>(null);
  const [currentRecord, setCurrentRecord] = useState<IRBACRole | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [filterTable, setFilterTable] = useState<ReadonlyArray<IRBACRole>>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);

  const typePriority: any = {
    owner: 1,
    admin: 2,
    editor: 3,
    viewer: 4,
    custom: 5,
  };

  useEffect(() => {
    auditReadLog(IAuditItem.ViewOrganizationRoles);
  }, []);

  useEffect(() => {
    const rolesList = Object.assign([], props.rolesList);
    rolesList.sort((a: IRBACRole, b: IRBACRole) => {
      return typePriority[a.type] - typePriority[b.type];
    });
    setFilterTable(rolesList);
  }, [props.rolesList]);

  const onDrawerClose = () => {
    setEditType(null);
    setCurrentRecord(null);
  };

  const renderDrawer = () => {
    if (editType) {
      return (
        <RolesDrawer
          onClose={onDrawerClose}
          type={editType}
          currentRecord={currentRecord}
          studioId={props.studioId}
          onUpdate={props.onUpdate}
          permissionsList={props.permissionsList}
          setEditType={setEditType}
        />
      );
    }
    return null;
  };

  const COLUMNS = [
    {
      name: getFormatMessage('role_name'),
      key: 'roleId',
      tableExtension: {
        renderRow(_key: any, _value: number) {
          const roleItem =
            props.rolesList.filter((item) => item.roleId === _value) || [];
          if (roleItem.length !== 0) {
            let { roleName } = roleItem[0];
            if (roleItem[0].type !== RbacRoleType.Custom) {
              roleName = roleName.slice(0, 1).toUpperCase() + roleName.slice(1);
            }
            return (
              <div style={{ minWidth: '188px' }}>
                <span
                  style={{ cursor: 'pointer', color: '#5764F1' }}
                  onClick={() => {
                    setEditType(RolesDrawerType.View);
                    auditReadLog(IAuditItem.ViewOrganizationRole);
                    setCurrentRecord(roleItem[0]);
                  }}
                >
                  {roleName}
                </span>
                {roleItem[0].type !== RbacRoleType.Custom && (
                  <span className={styles.systemIcon}>
                    {getFormatMessage('access_right_system')}
                  </span>
                )}
              </div>
            );
          }
          return '';
        },
      },
    },
    {
      name: getFormatMessage('description'),
      key: 'roleId',
      tableExtension: {
        width: 500,
        renderRow(_key: any, _value: number) {
          const roleItem =
            props.rolesList.filter((item) => item.roleId === _value) || [];
          let desc = '';
          if (roleItem.length !== 0) {
            if (roleItem[0].type === RbacRoleType.Owner) {
              desc = getFormatMessage('role_owner_desc');
            } else if (roleItem[0].type === RbacRoleType.Admin) {
              desc = getFormatMessage('role_admin_desc');
            } else if (roleItem[0].type === RbacRoleType.Editor) {
              desc = getFormatMessage('role_editor_desc');
            } else if (roleItem[0].type === RbacRoleType.Viewer) {
              desc = getFormatMessage('role_viewer_desc');
            } else {
              desc = roleItem[0].desc;
            }
          }
          return (
            <CustomPopover content={desc} placement="bottomLeft">
              <div className={styles.descItem}>{desc || '--'}</div>
            </CustomPopover>
          );
        },
      },
    },
    // {
    //   title: getFormatMessage('permissions'),
    //   dataIndex: 'permissions',
    //   key: 'permissions',
    //   minWidth: 370,
    //   render: (value: string[]) => {
    //     const valueName: string[] = [];
    //     value.forEach((item) => {
    //       const valueItem =
    //         props.permissionsList?.objects.filter(
    //           (it) => it.objectKey === item
    //         ) || [];
    //       if (valueItem.length !== 0) {
    //         valueName.push(valueItem[0].objectName);
    //       }
    //     });
    //     const permissions = valueName.length !== 0 ? valueName.join(',') : '';
    //     return <span>{permissions}</span>;
    //   },
    // },
    {
      name: getFormatMessage('related_group'),
      key: 'roleId',
      tableExtension: {
        width: 200,
        renderRow(_key: any, _value: number) {
          let relatedGroupNum = 0;
          props.groupsList.forEach((item) => {
            const relatedGroup = item.roles.filter(
              (it) => it.roleId === _value
            );
            if (relatedGroup.length !== 0) {
              relatedGroupNum += 1;
            }
          });
          return <span>{relatedGroupNum}</span>;
        },
      },
    },
    {
      name: getFormatMessage('operate'),
      key: 'action',
      tableExtension: {
        renderRow(_key: any, _value: any, row: IListItem) {
          const record = row as IRBACRole;
          return renderDefaultOperate(record);
        },
      },
    },
  ];

  const renderDefaultOperate = (record: IRBACRole): React.ReactNode => {
    let relatedGroupNum = 0;
    props.groupsList.forEach((item) => {
      const relatedGroup = item.roles.filter(
        (it) => it.roleId === record.roleId
      );
      if (relatedGroup.length !== 0) {
        relatedGroupNum += 1;
      }
    });
    if (record.type !== RbacRoleType.Custom) {
      return <span>--</span>;
    }
    return (
      <Space size="middle" className={classNames('operate-btn')}>
        <div className="webkit-box flex-left">
          <PermissionCheck
            permissionKey={RbacRoleObjectKey.RoleCreate}
            tipsPlacement="bottomLeft"
          >
            <a
              style={{
                marginRight: '10px',
              }}
              onClick={() => {
                setEditType(RolesDrawerType.Edit);
                setCurrentRecord(record);
              }}
            >
              {getFormatMessage('edit')}
            </a>
          </PermissionCheck>
          <PermissionCheck
            permissionKey={RbacRoleObjectKey.RoleDelete}
            tipsPlacement="bottomLeft"
            otherTips={
              relatedGroupNum !== 0
                ? getFormatMessage('cannot_delete_custom_role_desc_tips')
                : ''
            }
          >
            {relatedGroupNum !== 0 ? (
              <a
                style={{
                  color: '#F54E4E',
                  opacity: 0.3,
                  cursor: 'not-allowed',
                  marginLeft: '10px',
                }}
              >
                {getFormatMessage('delete')}
              </a>
            ) : (
              <a
                style={{ color: '#F54E4E', marginLeft: '10px' }}
                onClick={() => {
                  setCurrentRecord(record);
                  setShowDeleteModal(true);
                }}
              >
                {getFormatMessage('delete')}
              </a>
            )}
          </PermissionCheck>
        </div>
      </Space>
    );
  };

  const renderDeleteModal = () => {
    if (showDeleteModal) {
      return (
        <CommonOperateModal
          okText={getFormatMessage('confirm')}
          okButtonProps={{
            danger: true,
          }}
          className="delete-modal"
          onClose={() => {
            onDeleteCancel();
          }}
          onOk={onDeleteOk}
          title={getFormatMessage('delete_role')}
          context={
            <>
              <div className="webkit-box flex-start">
                {getFormatMessage('delete_role_desc')}
              </div>
            </>
          }
        />
      );
    }
    return null;
  };

  const onDeleteCancel = () => {
    setCurrentRecord(null);
    setShowDeleteModal(false);
  };

  const onDeleteOk = async () => {
    if (currentRecord?.roleId) {
      const body = {
        studioID: props.studioId,
      };
      const res = await deleteRoles(currentRecord.roleId, body);
      auditWriteLog(IAuditItem.DeleteOrganizationRole, {
        detail: {
          roleId: currentRecord.roleId,
          studioID: props.studioId,
        },
      });
      if (res) {
        messageSuccess(
          getFormatMessage('studio_setting_delete_success', {
            params: {
              studioName: currentRecord.roleName,
            },
          })
        );
        props.onUpdate();
        onDeleteCancel();
        return true;
      }
    }
    onDeleteCancel();
    messageError(getFormatMessage('delete_studio_failed'));
    return false;
  };

  const onSearch = (e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    if (value) {
      const reg = new RegExp(value, 'i');
      const arr: IRBACRole[] = [];
      props.rolesList.forEach((item) => {
        if (item.roleName && reg.test(item.roleName)) {
          arr.push(item);
        }
      });
      const rolesList = Object.assign([], arr);
      rolesList.sort((a: IRBACRole, b: IRBACRole) => {
        return typePriority[a.type] - typePriority[b.type];
      });
      setFilterTable(rolesList);
    } else {
      const rolesList = Object.assign([], props.rolesList.slice());
      rolesList.sort((a: IRBACRole, b: IRBACRole) => {
        return typePriority[a.type] - typePriority[b.type];
      });
      setFilterTable(rolesList);
    }
  };

  return (
    <Space direction="vertical" style={{ width: '100%' }} size={0}>
      <div
        className={classNames(
          'webkit-box flex-justify-space-between',
          styles.membersTitle
        )}
      >
        <div className={styles.titleLeft}>
          <PermissionCheck permissionKey={RbacRoleObjectKey.RoleCreate}>
            <Button
              onClick={() => setEditType(RolesDrawerType.Create)}
              className="gc-btn gc-btn-primary"
              icon={
                <SvgIcon
                  className="in-game-ant-icon"
                  icon={SvgIconName.InviteAddIcon}
                />
              }
            >
              {getFormatMessage('add_new_role')}
            </Button>
          </PermissionCheck>
        </div>
        <div className="webkit-box flex-center">
          <Input
            placeholder={getFormatMessage('search_role')}
            className="search-input"
            prefix={
              <img
                src={inputSearch}
                className={classNames('icon', styles.inputSearch)}
              />
            }
            onChange={onSearch}
            allowClear
            spellCheck="false"
          />
        </div>
      </div>
      <AutoTable
        // rowKey={(record) => record.roleId}
        list={filterTable}
        configFields={COLUMNS}
        className={styles.applicationTable}
        loading={props.loading}
        tableExtension={{
          rowKey: 'roleId',
          pagination: {
            current: currentPage,
            total: filterTable.length,
            pageSize,
            showTotal: (total) => {
              return getFormatMessage('total_items', {
                params: {
                  total,
                },
              });
            },
          },
          onChange: (changeInfo) => {
            if (!changeInfo) {
              return;
            }
            setCurrentPage(changeInfo.current);
            setPageSize(changeInfo.pageSize || 10);
          },
        }}
      />
      {renderDrawer()}
      {renderDeleteModal()}
    </Space>
  );
};
